// src/services/rateLimit.js
// Client-side rate limiter using localStorage.
// Mirrors the RATE_LIMITS config from config.js.
//
// Purpose: prevent accidental hammering and give immediate UX feedback.
// The server enforces the same limits independently — this is defense in depth.
//
// Usage:
//   const { allowed, remainingMs, remaining } = checkRateLimit("report");
//   if (!allowed) {
//     const wait = Math.ceil(remainingMs / 60000);
//     toast(`Too many reports. Try again in ${wait} minute(s).`);
//   }

import { RATE_LIMITS } from '@config';

const PREFIX = 'eos-rate-';

function getKey(action) { return `${PREFIX}${action}`; }

function getRecord(action) {
  try {
    const raw = localStorage.getItem(getKey(action));
    return raw ? JSON.parse(raw) : { timestamps: [] };
  } catch { return { timestamps: [] }; }
}

function saveRecord(action, record) {
  try { localStorage.setItem(getKey(action), JSON.stringify(record)); } catch {}
}

/**
 * Check if an action is rate-limited.
 * @returns {{ allowed: boolean, remaining: number, remainingMs: number }}
 */
export function checkRateLimit(action) {
  const limit = RATE_LIMITS[action];
  if (!limit) return { allowed: true, remaining: 999, remainingMs: 0 };

  const now    = Date.now();
  const record = getRecord(action);

  // Remove timestamps outside the window
  record.timestamps = record.timestamps.filter(ts => now - ts < limit.windowMs);

  const count      = record.timestamps.length;
  const allowed    = count < limit.max;

  // If at limit, how long until the oldest timestamp expires?
  const oldestTs   = record.timestamps[0] ?? now;
  const remainingMs = allowed ? 0 : Math.max(0, (oldestTs + limit.windowMs) - now);

  return {
    allowed,
    remaining:   Math.max(0, limit.max - count),
    remainingMs,
    remainingMin: Math.ceil(remainingMs / 60_000),
  };
}

/**
 * Record that an action was performed. Call AFTER success.
 */
export function recordAction(action) {
  const limit = RATE_LIMITS[action];
  if (!limit) return;

  const now    = Date.now();
  const record = getRecord(action);
  record.timestamps = [
    ...record.timestamps.filter(ts => now - ts < limit.windowMs),
    now,
  ];
  saveRecord(action, record);
}

/**
 * Convenience: check + record in one call.
 * Returns { allowed } — records action if allowed.
 */
export function consumeRateLimit(action) {
  const result = checkRateLimit(action);
  if (result.allowed) recordAction(action);
  return result;
}

/**
 * Get a human-readable message for a rate-limited action.
 */
export function getRateLimitMessage(action) {
  const result = checkRateLimit(action);
  if (result.allowed) return null;
  const limit = RATE_LIMITS[action];
  const wait  = result.remainingMin;
  return wait <= 1
    ? `Too many ${action} requests. Please wait a moment.`
    : `Too many ${action} requests. Try again in ${wait} minutes.`;
}
